"use client"

import { useMemo, useState } from "react"
import { useRouter } from "next/navigation"
import Image from "next/image"
import { Icons } from "@/components/Icons"
import DataTable from "@/components/ui/DataTable"
import type { DataTableColumn } from "@/components/ui/DataTable.types"
import FilterBar from "@/components/ui/FilterBar"
import type { FilterConfig } from "@/components/ui/FilterBar.types"
import { Button } from "@/components/ui/button"
import { test, liveMonitor } from "@/assets"

const publicGameFilters: FilterConfig[] = [
  {
    key: "subject",
    label: "หมวดหมู่ / วิชา",
    options: [
      { value: "", label: "หมวดหมู่ / วิชา" },
      { value: "ภาษา ก", label: "ภาษา ก" },
      { value: "คณิตศาสตร์", label: "คณิตศาสตร์" },
      { value: "วิทยาศาสตร์", label: "วิทยาศาสตร์" },
      { value: "สังคมศึกษา", label: "สังคมศึกษา" },
    ]
  },
  {
    key: "group",
    label: "กลุ่ม / หัวข้อ",
    placeholder: "กลุ่ม / หัวข้อ",
    options: [
      { value: "", label: "กลุ่ม / หัวข้อ" },
      { value: "ห นำ", label: "ห นำ" },
      { value: "สระ", label: "สระ" },
      { value: "วรรค", label: "วรรค" },
      { value: "การบวก", label: "การบวก" },
    ]
  },
  {
    key: "gameType",
    label: "ประเภทเกมส์",
    placeholder: "ประเภทเกมส์",
    options: [
      { value: "", label: "ประเภทเกมส์" },
      { value: "find the match", label: "find the match" },
      { value: "quiz", label: "quiz" },
      { value: "memory", label: "memory" },
      { value: "puzzle", label: "puzzle" },
    ]
  },
  {
    key: "questionType",
    label: "ประเภทคำถาม",
    placeholder: "ทั้งหมด",
    options: [
      { value: "", label: "ประเภทคำถาม" },
      { value: "คำถามชุด", label: "คำถามชุด" },
      { value: "คำถามเดี่ยว", label: "คำถามเดี่ยว" },
    ]
  },
  {
    key: "gameScope",
    label: "ทั้งหมดเกมส์ของฉัน",
    placeholder: "ทั้งหมด ",
    options: [
      { value: "", label: "เกมส์ทั้งหมด" },
      { value: "เกมส์ของฉัน", label: "เกมส์ของฉัน" },
      { value: "เกมส์ของทั้งหมด", label: "เกมส์ของทั้งหมด" },
    ]
  }
]

// PublicGame type
type PublicGame = {
  id: number
  name: string
  image?: string | null
  subject: string
  topic: string
  questionType: string
  gameType: string
  gameScope: string
  createdDate: string
}

// Mock data
const mockData: PublicGame[] = [
  {
    id: 1,
    name: "จับคู่คำศัพท์ ห นำ",
    image: test.src,
    subject: "ภาษา ก",
    topic: "ห นำ",
    questionType: "คำถามเดี่ยว",
    gameType: "find the match",
    gameScope: "เกมส์ของฉัน",
    createdDate: "251222",
  },
  {
    id: 2,
    name: "ป1_ก หน้า",
    image: null,
    subject: "ภาษา ก",
    topic: "ห นำ",
    questionType: "คำถามชุด",
    gameType: "quiz",
    gameScope: "เกมส์ทั้งหมด",
    createdDate: "251222",
  },
]

// Columns definition
const gameColumns: DataTableColumn<PublicGame>[] = [
  {
    key: "_index",
    label: "",
    width: "48px",
    render: (_value: number, _item: PublicGame, index: number) => index + 1,
  },
  {
    key: "name",
    label: "ชื่อแบบฝึกหัด",
    render: (_value: string, item: PublicGame) => (
      <div className="flex items-center gap-3">
        <span className="max-w-[200px] truncate text-[13px]">{item.name}</span>
        {item.image && (
          <div className="relative h-[26px] w-[46px] shrink-0 overflow-hidden rounded border border-gray-200 shadow-sm">
            <Image src={item.image} alt={item.name} fill className="object-cover" sizes="46px" />
          </div>
        )}
      </div>
    ),
  },
  {
    key: "subject",
    label: "วิชา",
  },
  {
    key: "topic",
    label: "หัวข้อ",
  },
  {
    key: "questionType",
    label: "ประเภทคำถาม/ชุด",
  },
  {
    key: "gameType",
    label: "ประเภทเกมส์",
  },
  {
    key: "createdDate",
    label: "วันที่สร้าง",
  },
  {
    key: "_preview",
    label: "preview",
    render: () => (
      <span className="text-[13px] text-gray-600">preview</span>
    ),
  },
  {
    key: "_liveMonitor",
    label: "dashboard preview",
    render: () => (
      <button className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#0092d7] hover:text-[#007fc0] transition-colors">
        <Image src={liveMonitor} alt="Live Monitor" width={17} height={17} className="shrink-0" />
        Live Monitor
      </button>
    ),
  },
  {
    key: "_actions",
    label: "",
    width: "120px",
    render: () => (
      <div className="flex items-center justify-end gap-3 text-gray-500">
        <button className="hover:text-gray-800 transition-colors">
          <Icons.InfoCircle width={17} height={17} strokeWidth={2} />
        </button>
        <button className="hover:text-gray-800 transition-colors">
          <Icons.DeleteTrash width={17} height={17} strokeWidth={2} />
        </button>
        <button className="hover:text-gray-800 transition-colors">
          <Icons.EditPen width={17} height={17} strokeWidth={2} />
        </button>
        <button className="hover:text-gray-800 transition-colors">
          <Icons.CopyDuplicate width={17} height={17} strokeWidth={2} />
        </button>
      </div>
    ),
  },
]

export default function Page() {
  const router = useRouter()

  const [search, setSearch] = useState("")
  const [filterValues, setFilterValues] = useState<Record<string, string>>({})

  // Handle filter changes
  const handleFilterChange = (key: string, value: string) => {
    setFilterValues(prev => ({ ...prev, [key]: value }))
  }

  const filteredData = useMemo(() => {
    const keyword = search.trim().toLowerCase()

    return mockData.filter((item) => {
      const searchMatch =
        keyword.length === 0 ||
        item.name.toLowerCase().includes(keyword) ||
        item.subject.toLowerCase().includes(keyword) ||
        item.topic.toLowerCase().includes(keyword)

      const subjectMatch = !filterValues.subject || filterValues.subject === "" || item.subject === filterValues.subject
      const groupMatch = !filterValues.group || filterValues.group === "" || item.topic === filterValues.group
      const typeMatch = !filterValues.gameType || filterValues.gameType === "" || item.gameType === filterValues.gameType
      const questionMatch = !filterValues.questionType || filterValues.questionType === "" || item.questionType === filterValues.questionType
      const scopeMatch = !filterValues.gameScope || filterValues.gameScope === "" || item.gameScope === filterValues.gameScope

      return searchMatch && subjectMatch && groupMatch && typeMatch && questionMatch && scopeMatch
    })
  }, [search, filterValues])



  return (
    <>
      <div className="flex w-full h-full flex-col bg-white rounded-2xl px-6 py-9 overflow-visible">
        {/* Header */}
        <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <h1 className="text-xl font-bold text-[#3e3e3e] md:text-3xl">
            คลังบทเรียนทั้งหมด / Pila Thailand Library
          </h1>
          <div className="flex flex-wrap gap-3">
            <Button className="h-11 rounded-xl bg-primary px-6 text-base font-semibold text-sidebar-accent-foreground hover:bg-primary/90 md:text-lg">
              + เพิ่มหมวดหมู่
            </Button>
            <Button className="h-11 rounded-xl bg-primary px-6 text-base font-semibold text-sidebar-accent-foreground hover:bg-primary/90 md:text-lg">
              + เพิ่มกลุ่ม
            </Button>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="relative">
          <FilterBar
            filters={publicGameFilters}
            values={filterValues}
            onChange={handleFilterChange}
            searchValue={search}
            onSearchChange={setSearch}
          />
        </div>

        {/* Data Table */}
        <div className="relative mt-4 flex-1">
          <DataTable
            columns={gameColumns}
            data={filteredData}

            emptyState={
              <div className="text-center py-8">
                <p className="text-gray-500">ไม่พบข้อมูล</p>
              </div>
            }
          />
        </div>
      </div>
    </>
  )
}
